import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { panelTheme } from '../PanelComerciales.theme';

interface EditableCellProps {
    value: string;
    onCommit: (next: string) => void;
    placeholder?: string;
}

const Editable = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    border-bottom: 1px dashed transparent;
    padding: 1px 4px;
    border-radius: 4px;

    &:hover {
        background: ${panelTheme.colors.surfaceMuted};
        border-bottom-color: ${panelTheme.colors.borderStrong};
    }

    input {
        border: 1px solid ${panelTheme.colors.borderStrong};
        border-radius: 4px;
        font: inherit;
        padding: 2px 6px;
        max-width: 140px;
    }
`;

const EditableCell: React.FC<EditableCellProps> = ({ value, onCommit, placeholder }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => setDraft(value), [value]);

    useEffect(() => {
        if (editing) inputRef.current?.focus();
    }, [editing]);

    const commit = () => {
        if (draft !== value) onCommit(draft);
        setEditing(false);
    };

    const cancel = () => {
        setDraft(value);
        setEditing(false);
    };

    if (!editing) {
        return (
            <Editable onClick={() => setEditing(true)}>
                <span>{value || placeholder || '—'}</span>
            </Editable>
        );
    }

    return (
        <Editable>
            <input
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') commit();
                    if (e.key === 'Escape') cancel();
                }}
            />
        </Editable>
    );
};

export default EditableCell;
